import { useState } from 'react';
import { useGameStore } from '../store';
import { RARITY } from '../data/cards';

// Rarity colors matching the design spec
const RARITY_COLORS = {
  [RARITY.COMMON]: { bg: 'bg-gray-600', text: 'text-gray-300', border: 'border-gray-500' },
  [RARITY.RARE]: { bg: 'bg-blue-600', text: 'text-blue-300', border: 'border-blue-500' },
  [RARITY.EPIC]: { bg: 'bg-purple-600', text: 'text-purple-300', border: 'border-purple-500' },
  [RARITY.LEGENDARY]: { bg: 'bg-yellow-500', text: 'text-yellow-300', border: 'border-yellow-400' },
};

export function DailyDraw() {
  const lastDrawDate = useGameStore((state) => state.lastDrawDate);
  const gameDate = useGameStore((state) => state.gameDate);
  const claimDailyDraw = useGameStore((state) => state.claimDailyDraw);

  const [revealedCard, setRevealedCard] = useState(null);
  const [isRevealing, setIsRevealing] = useState(false);

  const isAvailable = lastDrawDate !== gameDate;

  const handleDraw = () => {
    if (!isAvailable || isRevealing) return;

    setIsRevealing(true);

    // Small delay for anticipation
    setTimeout(() => {
      const result = claimDailyDraw();
      if (result.success) {
        setRevealedCard(result.card);
      }
      setIsRevealing(false);
    }, 500);
  };

  const handleDismiss = () => {
    setRevealedCard(null);
  };

  // Card reveal modal
  if (revealedCard) {
    const colors = RARITY_COLORS[revealedCard.rarity] || RARITY_COLORS[RARITY.COMMON];
    const slotText = revealedCard.isActive ? 'Active Slot' : 'Vault';

    return (
      <div className="w-full max-w-md mb-6">
        <div className={`
          border-2 ${colors.border} rounded-lg p-4 bg-gray-800
          animate-[fadeIn_0.3s_ease-out]
        `}>
          {/* Header */}
          <div className="text-center mb-3">
            <span className="text-gray-400 font-pixel text-xs">DAILY DRAW</span>
          </div>

          {/* Card Content */}
          <div className={`
            border ${colors.border} rounded p-4 mb-3
            ${colors.bg} bg-opacity-20
          `}>
            {/* Rarity Badge */}
            <div className="flex justify-between items-center mb-2">
              <span className={`${colors.text} font-pixel text-xs uppercase`}>
                {revealedCard.rarity}
              </span>
              <span className="text-gold font-pixel text-sm">+{revealedCard.reward} M</span>
            </div>

            {/* Card Name */}
            <h3 className="text-white font-pixel text-lg mb-2">{revealedCard.name}</h3>

            {/* Description */}
            <p className="text-gray-300 font-pixel text-xs leading-relaxed">
              {revealedCard.description}
            </p>
          </div>

          {/* Slot Destination */}
          <div className="text-center mb-3">
            <span className="text-teal font-pixel text-sm">
              Added to {slotText}
            </span>
          </div>

          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            className="w-full py-2 px-4 font-pixel text-sm bg-gray-700 text-gray-300 border border-gray-600 rounded hover:bg-gray-600 transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  // Draw available state
  if (isAvailable) {
    return (
      <div className="w-full max-w-md mb-6">
        <button
          onClick={handleDraw}
          disabled={isRevealing}
          className={`
            w-full py-4 px-6 font-pixel text-lg
            bg-gradient-to-r from-teal to-emerald-500
            text-dark border-2 border-teal rounded-lg
            hover:from-teal hover:to-teal
            transition-all duration-300
            ${isRevealing ? 'opacity-50 cursor-not-allowed' : 'animate-pulse hover:animate-none'}
          `}
        >
          {isRevealing ? 'Drawing...' : 'Daily Draw Available!'}
        </button>
      </div>
    );
  }

  // Already drawn today
  return (
    <div className="w-full max-w-md mb-6">
      <div className="py-3 px-4 font-pixel text-sm text-center bg-gray-800 text-gray-500 border border-gray-700 rounded-lg">
        Next draw tomorrow
      </div>
    </div>
  );
}
